import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type Status = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface SoftwareModuleOption {
  id: number;
  title: string;
  link: string;
  software_title?: string;
}

interface SoftwareModuleDetailRow {
  id: number;
  software_module_id: number;
  software_module_title: string;
  module_link: string;
  software_title: string;
  title: string;
  ai_answer_blocks: string[];
  problems: string[];
  solutions: string[];
  how_it_works: string[];
  faqs: { question: string; answer: string }[];
  status: Status;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminSoftwareModuleDetails() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<SoftwareModuleDetailRow[]>([]);
  const [modules, setModules] = useState<SoftwareModuleOption[]>([]);
  const [selectedRow, setSelectedRow] =
    useState<SoftwareModuleDetailRow | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const selectedModule = modules.find(
    (item) => Number(item.id) === Number(activeRow?.software_module_id),
  );

  const moduleDetailFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      {
        key: "software_module_id",
        label: "Software Module",
        type: "select",
        options: modules.map((item) => ({
          label: item.software_title
            ? `${item.software_title} - ${item.title}`
            : item.title,
          value: item.id,
        })),
        colSpan: 2,
      },
      {
        key: "module_link_text",
        label: "Page Link",
        type: "readonly-text",
        colSpan: 2,
      },
      {
        key: "title",
        label: "Page Title",
        type: "text",
        colSpan: 2,
      },
      {
        key: "ai_answer_blocks",
        label: "AI Answer Block Section",
        type: "repeatable-card-textarea",
        colSpan: 2,
      },
      {
        key: "problems",
        label: "Problems Section",
        type: "repeatable-combo-list",
        colSpan: 2,
      },
      {
        key: "solutions",
        label: "Solutions Section",
        type: "repeatable-solution",
        imageFolder: "software-support",
        colSpan: 2,
      },
      {
        key: "how_it_works",
        label: "How It Works Section",
        type: "repeatable-card-textarea",
        colSpan: 2,
      },
      {
        key: "faqs",
        label: "FAQ Section",
        type: "repeatable-card-faq",
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 1,
      },
    ],
    [modules],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "software_title", label: "Software" },
    { key: "software_module_title", label: "Module" },
    { key: "title", label: "Page Title" },
    {
      key: "module_link",
      label: "Link",
      render: (row) =>
        row.module_link ? (
          <a href={row.module_link} target="_blank" rel="noopener noreferrer">
            {row.module_link}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "ai_answer_blocks",
      label: "AI Blocks",
      render: (row) =>
        Array.isArray(row.ai_answer_blocks) ? row.ai_answer_blocks.length : 0,
    },
    {
      key: "faqs",
      label: "FAQs",
      render: (row) => (Array.isArray(row.faqs) ? row.faqs.length : 0),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
  ];

  const normalizeArray = (value: any) => {
    if (Array.isArray(value)) return value;

    if (!value) return [""];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [""];
    } catch {
      return [value];
    }
  };

  const normalizeFaqs = (value: any) => {
    if (Array.isArray(value)) return value;

    if (!value) return [{ question: "", answer: "" }];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [{ question: "", answer: "" }];
    } catch {
      return [{ question: "", answer: "" }];
    }
  };

  const prepareRowForModal = (row: any) => {
    if (!row) {
      return {
        status: "active",
        ai_answer_blocks: [""],
        problems: [""],
        solutions: [{ title: "", image: null }],
        how_it_works: [""],
        faqs: [{ question: "", answer: "" }],
        module_link_text: "-",
      };
    }

    return {
      ...row,
      ai_answer_blocks: normalizeArray(row.ai_answer_blocks),
      problems: normalizeArray(row.problems),
      solutions: normalizeArray(row.solutions),
      how_it_works: normalizeArray(row.how_it_works),
      faqs: normalizeFaqs(row.faqs),
      module_link_text: row.module_link || selectedModule?.link || "-",
    };
  };

  const fetchModules = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/software/modules`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setModules(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Software Modules Error:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/software/module-details`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? prepareRowForModal(
                freshData.find((item: any) => item.id === prev.id),
              )
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Software Module Details Error:", error);
    }
  };

  useEffect(() => {
    fetchModules();
    fetchDetails();
  }, []);

  useEffect(() => {
    if (!activeRow?.software_module_id) return;

    const module = modules.find(
      (item) => Number(item.id) === Number(activeRow.software_module_id),
    );

    setActiveRow((prev: any) => ({
      ...prev,
      module_link_text: module?.link || prev?.module_link || "-",
    }));
  }, [activeRow?.software_module_id, modules]);

  const cleanTextArray = (value: any) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item) => String(item || "").trim())
      .filter((item) => item.length > 0);
  };

  const cleanFaqArray = (value: any) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item) => ({
        question: String(item?.question || "").trim(),
        answer: String(item?.answer || "").trim(),
      }))
      .filter((item) => item.question || item.answer);
  };

  const cleanSolutionsArray = (value: any) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item, index) => {
        const fieldName = `solutionImage_${index}`;

        return {
          title: String(item?.title || "").trim(),
          image: item?.image?.file
            ? `__FILE__:${fieldName}`
            : typeof item?.image === "string"
              ? item.image
              : null,
        };
      })
      .filter((item) => item.title || item.image);
  };

  const buildPayload = (formData: any) => {
    return {
      software_module_id: formData.software_module_id,
      title: formData.title,
      ai_answer_blocks: cleanTextArray(formData.ai_answer_blocks),
      problems: cleanTextArray(formData.problems),
      solutions: cleanSolutionsArray(formData.solutions),
      how_it_works: cleanTextArray(formData.how_it_works),
      faqs: cleanFaqArray(formData.faqs),
      status:
        formData.status === "inactive" || formData.status === "active"
          ? formData.status
          : "active",
    };
  };

  const handleSubmit = async (formData: any) => {
    try {
      const payload = buildPayload(formData);
      const submitData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (
          key === "ai_answer_blocks" ||
          key === "problems" ||
          key === "solutions" ||
          key === "how_it_works" ||
          key === "faqs"
        ) {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, String(value ?? ""));
        }
      });

      if (Array.isArray(formData.solutions)) {
        formData.solutions.forEach((item: any, index: number) => {
          if (item?.image?.file) {
            submitData.append(`solutionImage_${index}`, item.image.file);
          }
        });
      }

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/software/module-details`
          : `${BASE_URL}/admin/software/module-details/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: submitData,
      });

      const result = await res.json();

      if (result.success) {
        fetchDetails();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Software module detail added successfully."
              : "Software module detail updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save software module detail.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Software Module Detail Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving software module detail.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Software Module Detail",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/software/module-details/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchDetails();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Software module detail deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message:
                result.message || "Failed to delete software module detail.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Software Module Detail Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message:
              "Something went wrong while deleting software module detail.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Software Module Details"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(prepareRowForModal(null));
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(prepareRowForModal(row));
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(prepareRowForModal(row));
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="software_module_details"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Software Module Detail"
        fields={moduleDetailFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
        modalClassName="software-module-detail-modal"
        bodyClassName="software-module-detail-modal-body"
      />
    </div>
  );
}
